import { ModalDialog } from 'components/ModalDialog';
import { notificationsSlice } from 'features/notifications/notificationsSlice';
import { ActKc2StatusEnum, AddRatioToActKc2StrategyEnum, WorkOrderStatusEnum } from 'gql/graphql';
import { useProjectData } from 'hooks/useProjectData';
import { useSendGpnAnalyticsEvent } from 'hooks/useSendGpnAnalyticsEvent';
import { formatActTitle } from 'pages/Project/Common/ActKc2Title/ActKc2Title';
import { RadioGroup, Select, Text, Grid, GridItem } from 'uikit';

import * as R from 'ramda';
import React, { useCallback, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import styles from './AddCoefficientToActKC2.module.css';

interface IAddCoefficientToActKC2Props {
    actKC2List: any[];
    closeModal?: () => void;
    openModalStrategy?: (options: any) => void;
}

const getLabel = R.prop('label');

export const AddCoefficientToActKC2 = ({ openModalStrategy, actKC2List, closeModal }: IAddCoefficientToActKC2Props) => {
    const { id: projectId, orderId } = useParams();

    const { state } = useLocation();
    const dispatch = useDispatch();

    const { projectData } = useProjectData(projectId, false);

    const { sendGpnAnalyticsEvent } = useSendGpnAnalyticsEvent();

    const actKC2Items = useMemo(
        () =>
            actKC2List
                ?.filter(({ status }) => [ActKc2StatusEnum.InProgress, ActKc2StatusEnum.OnCheck].includes(status))
                .map(({ id, number, date }) => ({ id, label: formatActTitle(number, date) })) ?? [],
        [actKC2List],
    );

    const strategyItems = useMemo(
        () => [
            {
                id: AddRatioToActKc2StrategyEnum.DuplicateWithExtendedCoefficients,
                label: 'Дублирование расценки с расширенным составом коэффициентов',
            },
            {
                id: AddRatioToActKc2StrategyEnum.CorrectOriginal,
                label: 'Корректировка исходной расценки',
            },
        ],
        [],
    );

    const [actSelectedItem, setActSelectedItem] = useState(() => actKC2Items[0]);
    const [strategySelectedItem, setStrategySelectedItem] = useState(() => strategyItems[0]);
    const navigate = useNavigate();

    const handleActOnChange = useCallback(({ value }) => {
        setActSelectedItem(value);
    }, []);

    const handleStrategyOnChange = useCallback(({ value }) => {
        setStrategySelectedItem(value);
    }, []);

    const handleOnClose = useCallback(() => {
        if (closeModal) {
            closeModal();
        } else {
            navigate('../');
        }
    }, [closeModal, navigate]);

    const handleOnConfirm = useCallback(() => {
        if (actSelectedItem) {
            if (openModalStrategy) {
                openModalStrategy({
                    actId: actSelectedItem.id,
                    strategy: strategySelectedItem.id,
                });
            } else {
                navigate(`${actSelectedItem.id}/${strategySelectedItem.id}`, { replace: true, state });
            }
            sendGpnAnalyticsEvent({
                componentId: 'rim_act KC_add coef apply button_pressed',
                projectId,
                orderId: Number(orderId),
                addCoefficientActionType: strategySelectedItem.id,
                orderStatus: WorkOrderStatusEnum.InProgress,
                projectName: projectData?.name,
                projectType: projectData?.contractType,
                projectDO: projectData?.subsidiary?.name,
                methodology: projectData?.methodology,
            });
        } else {
            dispatch(
                notificationsSlice.actions.add({
                    key: `addCoefficient_${strategySelectedItem.id}`,
                    message: 'Добавление коэффициента невозможно, так как у выбранного объекта отсутствуют Акты КС2',
                    status: 'warning',
                }),
            );
        }
    }, [
        actSelectedItem,
        dispatch,
        navigate,
        openModalStrategy,
        orderId,
        projectData?.contractType,
        projectData?.methodology,
        projectData?.name,
        projectData?.subsidiary?.name,
        projectId,
        sendGpnAnalyticsEvent,
        state,
        strategySelectedItem.id,
    ]);

    return (
        <ModalDialog
            isOpen
            title="Выбор Акта КС2"
            onClose={handleOnClose}
            onConfirm={handleOnConfirm}
            btnConfirmLabel="Применить"
        >
            <div className={styles.content}>
                <Grid cols="1" rowGap="s">
                    <GridItem>
                        <Text>Выберите Акт КС-2 для дальнейшей работы с коэффициентом к расценке</Text>
                    </GridItem>
                    <GridItem>
                        <Select onChange={handleActOnChange} items={actKC2Items} size="s" value={actSelectedItem} />
                    </GridItem>
                    <GridItem>
                        <RadioGroup
                            onChange={handleStrategyOnChange}
                            items={strategyItems}
                            value={strategySelectedItem}
                            getItemLabel={getLabel}
                        />
                    </GridItem>
                </Grid>
            </div>
        </ModalDialog>
    );
};

AddCoefficientToActKC2.displayName = 'AddCoefficientToActKC2';
